import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import {throwError as observableThrowError, lastValueFrom } from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiHelperService } from './api-helper.service';
import { GenericAPIArrayResponse, GenericAPIResponse, LoginApiResponse } from '../models/api-response';
import { CalibrationFocusRegion, CalibrationV2Candidate, CalibrationV2Job, CalibrationV2Status, CameraCommand, CameraStorageSnapshot, CaptureProfileName, GameVideoFileGroup, QwikcutCamera, QwikcutCameraCaptureProfile, QwikcutCameraCurrentRecordingData, QwikcutCameraDeviceModel, QwikcutCameraDriverHardware, QwikcutCameraRelease, QwikcutCameraReleaseArtifactKind, QwikcutCameraReleaseChannel, QwikcutCameraReleaseType, QwikcutCameraUpdateEvent, QwikcutCameraSoftware, QwikcutCameraSoftwareType, QwikcutCameraYoutubeAuthorization, QwikcutCameraYoutubeConnectionStatus, QwikcutCameraZrokSsh, RoiConfig, ScoreboardData, SeamConfig, SeamRuntimeConfig, SeamUpdateResult, TrackingMode, VirtualPtzGeometry } from '../models/qwikcut-camera';
import { Sport } from '../models/conference';
import { Game } from '../models/game';
import { Team } from '../models/team';

export interface DiagnosticFrameUploadResult {
  captureId: string;
  profile: string;
  keys: {left: string; right: string};
  timestamps: {left: string; right: string};
  syncDeltaMs: number;
  dimensions: {
    left: {width: number; height: number};
    right: {width: number; height: number};
  };
  uploads: {left: boolean; right: boolean};
}

export interface CalibrationStopResult {
  runtimeState: 'stopped';
  cleanupState: 'complete' | 'pending';
  cleanupError?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class QwikcutCameraService {

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelperService,
    private router: Router
  ) { }

  getCameraApiUrl(cameraId: string): string {
    return `https://${cameraId}.zrok.qwikcut.com`;
  }

  registerCameraToTeam(teamid: string, cameraId: string, label?: string): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/register`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, cameraId, label};

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  removeCameraFromTeam(teamid: string, cameraId: string): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/register`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, cameraId};

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createCamera(data: Partial<QwikcutCamera>): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {data};

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateCamera(cameraId: string, update: Partial<QwikcutCamera>): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {update};

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteCamera(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${encodeURIComponent(cameraId)}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  sendCameraCommandSocketio(cameraId: string, command: CameraCommand, data?: any): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {command, data};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getTeamCameras(teamid: string): Promise<GenericAPIArrayResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/team/${teamid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCamera>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getAllCameras(): Promise<GenericAPIArrayResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCamera>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCamera(cameraId: string): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCamera>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraCaptureProfile(cameraId: string): Promise<GenericAPIResponse<QwikcutCameraCaptureProfile>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}/capture-profile`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCameraCaptureProfile>>(url, {headers}).pipe(
      map(res => res),
      catchError(err => observableThrowError(err.message)),
    ));
  }

  getCameraStorage(cameraId: string): Promise<GenericAPIResponse<CameraStorageSnapshot | null>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${encodeURIComponent(cameraId)}/storage`;
    return lastValueFrom(this.http.get<GenericAPIResponse<CameraStorageSnapshot | null>>(url, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }).pipe(catchError(err => observableThrowError(err))));
  }

  queueCameraStorageUpload(cameraId: string, gameId: string): Promise<GenericAPIResponse<CameraStorageSnapshot>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${encodeURIComponent(cameraId)}/storage/uploads`;
    return lastValueFrom(this.http.post<GenericAPIResponse<CameraStorageSnapshot>>(url, {gameId}, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }).pipe(catchError(err => observableThrowError(err))));
  }

  deleteCameraStorageClip(cameraId: string, gameId: string, clipNumber: number): Promise<GenericAPIResponse<CameraStorageSnapshot>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${encodeURIComponent(cameraId)}/storage/games/${encodeURIComponent(gameId)}/clips/${clipNumber}`;
    return lastValueFrom(this.http.delete<GenericAPIResponse<CameraStorageSnapshot>>(url, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }).pipe(catchError(err => observableThrowError(err))));
  }

  setCameraCaptureProfile(cameraId: string, profile: CaptureProfileName): Promise<GenericAPIResponse<QwikcutCameraCaptureProfile>> {
    const url = `${this.getCameraApiUrl(cameraId)}/capture-profile`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCameraCaptureProfile>>(url, {profile}, {headers}).pipe(
      map(res => res),
      catchError(err => observableThrowError(err.message)),
    ));
  }

  getCameraZrokSsh(cameraId: string): Promise<GenericAPIResponse<QwikcutCameraZrokSsh>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}/zrok-ssh`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCameraZrokSsh>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createQwikcutCameraSoftware(name: string, type: QwikcutCameraSoftwareType, sportid?: Sport): Promise<GenericAPIResponse<QwikcutCameraSoftware>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {name, type, sportid};

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraSoftware>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  incrementQwikcutCameraSoftwareVersion(_id: string): Promise<GenericAPIResponse<QwikcutCameraSoftware>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCameraSoftware>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteQwikcutCameraSoftwareVersion(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getAllQwikcutCameraSoftware(): Promise<GenericAPIArrayResponse<QwikcutCameraSoftware>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraSoftware>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getQwikcutCameraReleases(): Promise<GenericAPIArrayResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraRelease>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createQwikcutCameraReleaseUploadUrls(version: string, files: {filename: string, contentType?: string, kind?: QwikcutCameraReleaseArtifactKind}[], options?: {releaseType?: QwikcutCameraReleaseType, deviceModel?: QwikcutCameraDeviceModel, driverHardware?: QwikcutCameraDriverHardware, sportid?: string}): Promise<GenericAPIResponse<{bucket: string, prefix: string, urls: {filename: string, key: string, url: string}[]}>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/upload-url`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {version, files, ...(options || {})};

    return lastValueFrom(this.http.post<GenericAPIResponse<{bucket: string, prefix: string, urls: {filename: string, key: string, url: string}[]}>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  finalizeQwikcutCameraRelease(data: {version: string, releaseType?: QwikcutCameraReleaseType, deviceModel?: QwikcutCameraDeviceModel, driverHardware?: QwikcutCameraDriverHardware, sportid?: string, files?: {filename: string, contentType?: string, kind?: QwikcutCameraReleaseArtifactKind}[], channel?: QwikcutCameraReleaseChannel, notes?: string, targets?: {cameraIds?: string[], excludeCameraIds?: string[]}}): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/finalize`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraRelease>>(url, data, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateQwikcutCameraRelease(_id: string, update: Partial<QwikcutCameraRelease>): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {update};

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCameraRelease>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  publishQwikcutCameraRelease(_id: string): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${_id}/publish`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraRelease>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  disableQwikcutCameraRelease(_id: string): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${_id}/disable`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraRelease>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteQwikcutCameraRelease(_id: string): Promise<GenericAPIResponse<{release: QwikcutCameraRelease, deletedObjects: number, exactObjectsDeleted: number, prefixObjectsDeleted: number}>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<{release: QwikcutCameraRelease, deletedObjects: number, exactObjectsDeleted: number, prefixObjectsDeleted: number}>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getQwikcutCameraReleaseUpdateEvents(releaseId: string): Promise<GenericAPIArrayResponse<QwikcutCameraUpdateEvent>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${releaseId}/update-events`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraUpdateEvent>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  //directly communicate with the camera

  shutdownCamera(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/shutdown`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  rebootCamera(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/reboot`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  restartCameraSoftware(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/restart`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraCurrentRecording(cameraId: string): Promise<GenericAPIResponse<QwikcutCameraCurrentRecordingData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/current-recording`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCameraCurrentRecordingData>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraCurrentRecordings(cameraId: string): Promise<GenericAPIArrayResponse<Game>> {
    const url = `${this.getCameraApiUrl(cameraId)}/current-recordings`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<Game>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUpcomingGamesForCamera(cameraId: string, teamid?: string): Promise<GenericAPIArrayResponse<Game>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/games/${cameraId}?extendedRange=true${teamid != null ? `&teamid=${teamid}` : ''}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<Game>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setCameraRecordingEnd(cameraId: string, recordingEnd: Date): Promise<GenericAPIArrayResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/set-recording-end`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {recordingEnd};

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getDeviceIpAddress(cameraId: string): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/device-ip-address`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<string>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getDeviceTemps(cameraId: string): Promise<GenericAPIResponse<{cpu: number, gpu: number}>> {
    const url = `${this.getCameraApiUrl(cameraId)}/device-temps`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<{cpu: number, gpu: number}>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraLogs(cameraId: string, full: boolean = false): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/${full ? 'full-' : ''}logs`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  startCalibrationProcess(cameraId: string): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { };

    return lastValueFrom(this.http.post<GenericAPIResponse<string>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setCalibrationMode(cameraId: string, mode: 'stitch' | 'alignment' | 'align'): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration-mode`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { mode };

    return lastValueFrom(this.http.post<GenericAPIResponse<string>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCalibrationV2Status(cameraId: string): Promise<GenericAPIResponse<CalibrationV2Status>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/status`;
    return lastValueFrom(this.http.get<GenericAPIResponse<CalibrationV2Status>>(url, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  startCalibrationV2Job(
    cameraId: string,
    nfeatures = 12000,
    focusRegion?: CalibrationFocusRegion,
    virtualPtzGeometry?: VirtualPtzGeometry,
  ): Promise<GenericAPIResponse<CalibrationV2Job>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/jobs`;
    const body = {
      nfeatures,
      ...(focusRegion ? {focusRegion} : {}),
      ...(virtualPtzGeometry ? {virtualPtzGeometry} : {}),
    };
    return lastValueFrom(this.http.post<GenericAPIResponse<CalibrationV2Job>>(url, body, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  getCalibrationV2Job(cameraId: string, jobId: string): Promise<GenericAPIResponse<CalibrationV2Job>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/jobs/${encodeURIComponent(jobId)}`;
    return lastValueFrom(this.http.get<GenericAPIResponse<CalibrationV2Job>>(url, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  getCalibrationV2Candidate(cameraId: string, candidateId: string): Promise<GenericAPIResponse<CalibrationV2Candidate>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/candidates/${encodeURIComponent(candidateId)}`;
    return lastValueFrom(this.http.get<GenericAPIResponse<CalibrationV2Candidate>>(url, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  getCalibrationV2Artifact(cameraId: string, candidateId: string, artifact: string): Promise<Blob> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/candidates/${encodeURIComponent(candidateId)}/artifacts/${encodeURIComponent(artifact)}`;
    return lastValueFrom(this.http.get(url, {
      headers: this.apiHelper.apiHeaderWithToken(),
      responseType: 'blob',
    }));
  }

  previewCalibrationV2Candidate(cameraId: string, candidateId: string, timeoutSeconds = 60): Promise<GenericAPIResponse<any>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/candidates/${encodeURIComponent(candidateId)}/preview`;
    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, {timeoutSeconds}, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  cancelCalibrationV2Preview(cameraId: string): Promise<GenericAPIResponse<any>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/preview`;
    return lastValueFrom(this.http.delete<GenericAPIResponse<any>>(url, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  calibrationV2CandidateAction(cameraId: string, candidateId: string, action: 'accept' | 'reject'): Promise<GenericAPIResponse<any>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/candidates/${encodeURIComponent(candidateId)}/${action}`;
    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, {}, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  undoCalibrationV2(cameraId: string): Promise<GenericAPIResponse<any>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/v2/undo`;
    return lastValueFrom(this.http.post<GenericAPIResponse<any>>(url, {}, {
      headers: this.apiHelper.apiHeaderWithToken(),
    }));
  }

  stopCalibrationProcess(cameraId: string): Promise<GenericAPIResponse<CalibrationStopResult>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<CalibrationStopResult>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  recomputeCameraHomography(cameraId: string, nfeatures: number = 10240): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/recompute-homography`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { nfeatures };

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  resetCameraHomography(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/reset`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setRoi(cameraId: string, roiConfig: RoiConfig): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/set-roi`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {roiConfig};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setCropOffset(cameraId: string, type: 'top' | 'bottom', cropOffset: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/crop-offset`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, cropOffset};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setRotation(cameraId: string, type: 'left' | 'right', value: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/rotation`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, value};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setXShift(cameraId: string, type: 'left' | 'right', value: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/xshift`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, value};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setYShift(cameraId: string, type: 'left' | 'right', value: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/yshift`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, value};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
    }),));
  }

  getSeamConfig(cameraId: string): Promise<GenericAPIResponse<SeamRuntimeConfig>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/seam`;
    const headers = this.apiHelper.apiHeaderWithToken();
    return lastValueFrom(this.http.get<GenericAPIResponse<SeamRuntimeConfig>>(url, {headers}).pipe(
      map(res => res),
      catchError(err => observableThrowError(err?.error?.message || err.message)),
    ));
  }

  setSeamConfig(cameraId: string, value: SeamConfig): Promise<GenericAPIResponse<SeamUpdateResult>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/seam`;
    const headers = this.apiHelper.apiHeaderWithToken();
    return lastValueFrom(this.http.put<GenericAPIResponse<SeamUpdateResult>>(url, value, {headers}).pipe(
      map(res => res),
      catchError(err => observableThrowError(err?.error?.message || err.message)),
    ));
  }

  resetSeamConfig(cameraId: string): Promise<GenericAPIResponse<SeamUpdateResult>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/seam/reset`;
    const headers = this.apiHelper.apiHeaderWithToken();
    return lastValueFrom(this.http.post<GenericAPIResponse<SeamUpdateResult>>(url, {}, {headers}).pipe(
      map(res => res),
      catchError(err => observableThrowError(err?.error?.message || err.message)),
    ));
  }

  captureDiagnosticFrames(cameraId: string): Promise<GenericAPIResponse<DiagnosticFrameUploadResult>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/capture-diagnostic-frames`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<DiagnosticFrameUploadResult>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  captureRawFeeds(cameraId: string, gameid: string, recordTime?: number): Promise<GenericAPIResponse<undefined>> {
    const body = {gameid, recordTime};
    const url = `${this.getCameraApiUrl(cameraId)}/capture-raw-feeds`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  stopAllCaptures(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/stop-capture`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  startGameCapture(cameraId: string, gameid: string): Promise<GenericAPIResponse<undefined>> {
    const body = {gameid};
    const url = `${this.getCameraApiUrl(cameraId)}/start-capture`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  restartGameCapture(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/restart-capture`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setTrackingMode(cameraId: string, mode: TrackingMode): Promise<GenericAPIResponse<undefined>> {
    const body = {mode};
    const url = `${this.getCameraApiUrl(cameraId)}/tracking-mode`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  checkForUpdates(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/check-for-updates`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCurrentSoftware(cameraId: string): Promise<GenericAPIArrayResponse<QwikcutCameraSoftware>> {
    const url = `${this.getCameraApiUrl(cameraId)}/software`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraSoftware>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getRebootReason(cameraId: string): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/reboot-reason`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<string>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getExistingGameFiles(cameraId: string): Promise<GenericAPIArrayResponse<GameVideoFileGroup>> {
    const url = `${this.getCameraApiUrl(cameraId)}/game-files`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<GameVideoFileGroup>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  uploadExistingGameFiles(cameraId: string, gameid: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/game-files/upload`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {gameid};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteExistingGameFile(cameraId: string, filePath: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/game-files/delete`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {filePath};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getScoreboardData(cameraId: string): Promise<GenericAPIResponse<ScoreboardData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/scoreboard`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<ScoreboardData>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateScoreboard(cameraId: string, body: ScoreboardData): Promise<GenericAPIResponse<ScoreboardData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/scoreboard`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<ScoreboardData>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  resetScoreboard(cameraId: string): Promise<GenericAPIResponse<ScoreboardData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/scoreboard`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<ScoreboardData>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  registerYoutubeAccountForTeam(teamid: string, accessCode: string, redirectUri: string): Promise<GenericAPIResponse<Team>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/register-youtube`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, accessCode, redirectUri};

    return lastValueFrom(this.http.post<GenericAPIResponse<Team>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  unregisterYoutubeAccountForTeam(teamid: string): Promise<GenericAPIResponse<Team>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/unregister-youtube`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid};

    return lastValueFrom(this.http.post<GenericAPIResponse<Team>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

	getYoutubeConnectionStatus(teamid: string): Promise<GenericAPIResponse<QwikcutCameraYoutubeConnectionStatus>> {
		const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/youtube/status/${encodeURIComponent(teamid)}`;
		return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCameraYoutubeConnectionStatus>>(url, {
			headers: this.apiHelper.apiHeaderWithToken(),
		}).pipe(catchError(err => observableThrowError(err))));
	}

	createYoutubeAuthorization(teamid: string, redirectUri: string): Promise<GenericAPIResponse<QwikcutCameraYoutubeAuthorization>> {
		const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/youtube/authorization`;
		return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraYoutubeAuthorization>>(url, {
			teamid,
			redirectUri,
		}, {headers: this.apiHelper.apiHeaderWithToken()}).pipe(catchError(err => observableThrowError(err))));
	}

	completeYoutubeAuthorization(code: string, state: string, redirectUri: string): Promise<GenericAPIResponse<QwikcutCameraYoutubeConnectionStatus>> {
		const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/youtube/callback`;
		return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraYoutubeConnectionStatus>>(url, {
			code,
			state,
			redirectUri,
		}, {headers: this.apiHelper.apiHeaderWithToken()}).pipe(catchError(err => observableThrowError(err))));
	}

	disconnectYoutubeAccount(teamid: string): Promise<GenericAPIResponse<QwikcutCameraYoutubeConnectionStatus>> {
		const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/youtube/${encodeURIComponent(teamid)}`;
		return lastValueFrom(this.http.delete<GenericAPIResponse<QwikcutCameraYoutubeConnectionStatus>>(url, {
			headers: this.apiHelper.apiHeaderWithToken(),
		}).pipe(catchError(err => observableThrowError(err))));
	}

}
